<script setup lang="ts">
  import { computed, ref } from 'vue';
  import OrderIcon from '@/shared/assets/icons/Order.svg';
  import { CustomCheckbox, IconImage, SearchBar } from '@/shared/ui';
  import {
    areAllPermissionSubjectsSelected,
    areSomePermissionSubjectsSelected,
    permissionSubjectCountLabel,
    permissionSubjectEmptyMessage,
    permissionSubjectSelectAllLabel,
    resolveVisiblePermissionSubjects,
    togglePermissionSubjectId,
  } from '../../lib/permission-subject-filters';
  import type {
    PermissionSubject,
    PermissionSubjectMode,
  } from '../../composables/usePermissionSubjects';

  const props = withDefaults(
    defineProps<{
      mode: PermissionSubjectMode;
      items: PermissionSubject[];
      selectedIds?: string[];
      loading?: boolean;
      hasError?: boolean;
      disabled?: boolean;
    }>(),
    {
      selectedIds: () => [],
      loading: false,
      hasError: false,
      disabled: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:selectedIds', value: string[]): void;
  }>();

  const searchTerm = ref('');
  const sortAscending = ref(true);

  const selectAllLabel = computed(() => permissionSubjectSelectAllLabel(props.mode));
  const countLabel = computed(() => permissionSubjectCountLabel(props.mode));
  const emptyMessage = computed(() =>
    permissionSubjectEmptyMessage(props.mode, props.hasError, searchTerm.value.trim() !== '')
  );

  const visibleItems = computed(() =>
    resolveVisiblePermissionSubjects(props.items, searchTerm.value, sortAscending.value)
  );

  const allVisibleSelected = computed(() =>
    areAllPermissionSubjectsSelected(visibleItems.value, props.selectedIds)
  );

  const someVisibleSelected = computed(() =>
    areSomePermissionSubjectsSelected(visibleItems.value, props.selectedIds)
  );

  const isItemSelected = (item: PermissionSubject) => props.selectedIds.includes(String(item.id));

  const toggleSort = () => {
    sortAscending.value = !sortAscending.value;
  };

  const handleSearch = (term: string) => {
    searchTerm.value = term;
  };

  const toggleItem = (item: PermissionSubject) => {
    emit('update:selectedIds', togglePermissionSubjectId(props.selectedIds, String(item.id)));
  };

  const toggleAll = () => {
    const visibleIds = visibleItems.value.map((item) => String(item.id));

    emit(
      'update:selectedIds',
      allVisibleSelected.value
        ? props.selectedIds.filter((id) => !visibleIds.includes(id))
        : [...props.selectedIds, ...visibleIds.filter((id) => !props.selectedIds.includes(id))]
    );
  };
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-3" :data-testid="`permission-subject-list-${mode}`">
    <div class="flex items-center gap-4">
      <SearchBar class="min-w-0 flex-1" @search="handleSearch" />
      <button
        type="button"
        class="flex size-[42px] shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[var(--theme-secondary-ink)] text-[var(--theme-secondary-ink)] shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
        aria-label="Ordenar lista"
        :data-testid="`permission-subject-sort-${mode}`"
        @click="toggleSort"
      >
        <IconImage :src="OrderIcon" alt="Ordenar" class="h-4 w-3.5 shrink-0" />
      </button>
    </div>

    <div
      class="flex h-60 flex-col gap-3 overflow-y-auto rounded-2xl border border-border-color bg-white-01 pt-4 pr-2 pb-4 pl-4"
    >
      <label class="flex cursor-pointer items-center gap-3">
        <CustomCheckbox
          :id="`permission-subject-select-all-${mode}`"
          :checked="allVisibleSelected"
          :indeterminate="someVisibleSelected && !allVisibleSelected"
          :disabled="disabled || visibleItems.length === 0"
          @change="toggleAll"
        />
        <span class="text-sm leading-4 font-semibold text-gray-07">{{ selectAllLabel }}</span>
      </label>

      <p
        v-if="loading"
        class="text-sm text-gray-05"
        :data-testid="`permission-subject-loading-${mode}`"
      >
        Carregando...
      </p>

      <p
        v-else-if="visibleItems.length === 0"
        class="text-sm text-gray-05"
        :data-testid="`permission-subject-empty-${mode}`"
      >
        {{ emptyMessage }}
      </p>

      <template v-else>
        <label
          v-for="item in visibleItems"
          :key="item.id"
          class="flex cursor-pointer items-center gap-3"
        >
          <CustomCheckbox
            :id="`permission-subject-${mode}-${item.id}`"
            :value="String(item.id)"
            :checked="isItemSelected(item)"
            :disabled="disabled"
            @change="toggleItem(item)"
          />
          <span class="text-sm font-medium text-gray-07">{{ item.name }}</span>
        </label>
      </template>
    </div>

    <p class="text-sm text-gray-05">{{ countLabel }} {{ selectedIds.length }}</p>
  </div>
</template>
